import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, AlertCircle, Upload } from "lucide-react";

interface MercerCountyTextParserProps {
  onRecordsSaved?: () => void;
}

interface ImportResult {
  success: number;
  failed: number;
  failedRecords: Array<{ line: number; error: string }>;
}

const parseNames = (rawNames: string): { firstName: string; lastName: string } => {
  if (!rawNames || rawNames.trim() === '') {
    return { firstName: '', lastName: '' };
  }

  const names = rawNames.split(';')[0].trim();
  
  const cleanName = names
    .replace(/\s+TRUSTEE$/i, '')
    .replace(/\s+TR$/i, '')
    .replace(/\s+LLC$/i, '')
    .replace(/\s+INC$/i, '')
    .replace(/\s+TRUST$/i, '')
    .replace(/\s+JR$/i, '')
    .replace(/\s+SR$/i, '')
    .replace(/\s+II$/i, '')
    .replace(/\s+III$/i, '')
    .replace(/\s+IV$/i, '')
    .trim();
  
  if (cleanName.includes(' LLC') || cleanName.includes(' INC') || 
      cleanName.includes(' PROPERTIES') || cleanName.includes(' FARMS') ||
      cleanName.includes(' ENTERPRISES')) {
    return { firstName: cleanName, lastName: '' };
  }
  
  const parts = cleanName.split(/\s+/);
  
  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }
  
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  
  const filteredParts = parts.filter(part => part.length > 1 || parts.length === 2);
  
  if (filteredParts.length === 1) {
    return { firstName: filteredParts[0], lastName: '' };
  }
  
  const lastName = filteredParts[0];
  const firstName = filteredParts.slice(1).join(' ');
  
  return { firstName, lastName };
};

export const MercerCountyTextParser = ({ onRecordsSaved }: MercerCountyTextParserProps) => {
  const [pastedText, setPastedText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const parseRecords = (text: string) => {
    const lines = text.split('\n');
    const records = [];
    
    // Combine multi-line records first
    let currentRecord = '';
    const consolidatedLines: string[] = [];
    
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      
      // Check if this is a new record (starts with a number followed by tabs)
      if (/^\d+\t/.test(line)) {
        if (currentRecord) {
          consolidatedLines.push(currentRecord);
        }
        currentRecord = line;
      } else {
        // Continuation line - append to current record
        currentRecord += ' ' + trimmed;
      }
    }
    
    // Don't forget the last record
    if (currentRecord) {
      consolidatedLines.push(currentRecord);
    }
    
    console.log(`Consolidated ${lines.length} lines into ${consolidatedLines.length} records`);
    
    for (const line of consolidatedLines) {
      const parts = line.split('\t');
      
      // Find the document number - it should be a 12-digit number starting with 20XX
      let docNumberIndex = -1;
      let documentNumber = '';
      
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i]?.trim();
        if (part && /^20\d{10}$/.test(part)) {
          docNumberIndex = i;
          documentNumber = part;
          break;
        }
      }
      
      if (docNumberIndex === -1 || !documentNumber) {
        continue;
      }
      
      // Work backwards from document number
      const dateStr = parts[docNumberIndex - 5]?.trim() || '';
      const documentType = parts[docNumberIndex - 4]?.trim() || '';
      
      // Find "V" to locate seller and buyer
      let vIndex = parts.findIndex(p => p?.trim() === 'V');
      if (vIndex === -1) continue;
      
      const sellerName = parts[vIndex + 1]?.trim() || '';
      const buyerName = parts[vIndex + 2]?.trim() || '';
      const legalDescription = parts.slice(docNumberIndex + 1).join('\t').trim();
      
      // Parse date from MM/DD/YYYY format
      let filingDate = new Date().toISOString().split('T')[0];
      if (dateStr && dateStr.includes('/')) {
        const [month, day, year] = dateStr.split('/');
        if (month && day && year) {
          filingDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      
      const seller = parseNames(sellerName);
      const buyer = parseNames(buyerName);
      
      const grantorName = seller.lastName 
        ? `${seller.lastName}, ${seller.firstName}`.trim() 
        : seller.firstName;
      const granteeName = buyer.lastName 
        ? `${buyer.lastName}, ${buyer.firstName}`.trim() 
        : buyer.firstName;
      
      records.push({
        county: 'mercer',
        document_number: documentNumber.substring(0, 50),
        document_type: documentType === 'LAND CONT' ? 'LAND CONTRACT' : documentType,
        filing_date: filingDate,
        grantor_name: grantorName,
        grantee_name: granteeName,
        legal_description: legalDescription || null,
        is_potential_owner_finance: true,
        confidence_score: 95,
        address_extraction_method: 'manual_import',
        status: 'new',
        pdf_downloaded: false,
        pdf_parsed: false,
        ocr_attempted: false,
        address_needs_review: true,
        grantor_address: null,
        grantee_address: null,
        property_address: null
      });
    }
    
    return records;
  };

  const processText = async (text: string) => {
    if (!text.trim()) {
      toast.error('Please paste the record data first');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      const records = parseRecords(text);
      
      if (records.length === 0) {
        toast.error('No valid records found in the pasted text');
        setIsProcessing(false);
        return;
      }

      console.log('Attempting to insert', records.length, 'records');
      
      const result: ImportResult = {
        success: 0,
        failed: 0,
        failedRecords: []
      };

      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        setProgress(Math.round(((i + 1) / records.length) * 100));

        try {
          // Check for duplicate
          const { data: existing } = await supabase
            .from('county_research')
            .select('id')
            .eq('county', 'mercer')
            .eq('document_number', record.document_number)
            .maybeSingle();

          if (existing) {
            console.log(`Skipping duplicate: ${record.document_number}`);
            result.failed++;
            result.failedRecords.push({
              line: i + 1,
              error: 'Duplicate record'
            });
            continue;
          }

          const { error } = await supabase
            .from('county_research')
            .insert(record);

          if (error) {
            console.error('Insert error for record', i, ':', error);
            result.failed++;
            result.failedRecords.push({
              line: i + 1,
              error: error.message
            });
          } else {
            result.success++;
          }
        } catch (err) {
          console.error('Error processing record', i, ':', err);
          result.failed++;
          result.failedRecords.push({
            line: i + 1,
            error: String(err)
          });
        }
      }

      setImportResult(result);
      
      if (result.success > 0) {
        toast.success(`Successfully imported ${result.success} records`);
        setPastedText('');
        onRecordsSaved?.();
      }
      
      if (result.failed > 0) {
        toast.warning(`${result.failed} records failed to import`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      await processText(text);
    };
    reader.readAsText(file);
  };

  const handlePasteImport = () => {
    processText(pastedText);
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Import completed: {importResult.success} succeeded, {importResult.failed} failed
            {importResult.failedRecords.length > 0 && (
              <div className="mt-2 text-sm">
                Failed records: {importResult.failedRecords.map(r => `Line ${r.line}`).join(', ')}
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Mercer County Text Import</CardTitle>
          <CardDescription>
            Paste tab-delimited text from Mercer County search results. 
            <br />Expected columns: Row # → (blank) → (blank) → V → Seller → Buyer → Date → Doc Type → OR → (blank) → 0000 → Doc Number → Legal Description
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Upload Text File</label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".txt"
                onChange={handleFileUpload}
                disabled={isProcessing}
                className="block w-full text-sm"
              />
              <Upload className="h-4 w-4" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Or Paste Text Data</label>
            <Textarea
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              placeholder="Paste Mercer County records here..."
              className="min-h-[400px] font-mono text-sm"
              disabled={isProcessing}
            />
            <Button 
              onClick={handlePasteImport}
              disabled={isProcessing || !pastedText.trim()}
              className="w-full"
            >
              {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Import Pasted Data
            </Button>
          </div>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-center text-muted-foreground">
                Processing... {progress}%
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Expected Format:</strong> Tab-separated columns:
          <br />Row → ... → V → Seller Name → Buyer Name → Date → Doc Type → OR → 0000 → Doc Number → Legal Description
          <br />
          <br />Names will be automatically parsed to separate first and last names, with middle initials removed.
        </AlertDescription>
      </Alert>
    </div>
  );
};
